import { CraftArtifact, useImageCropper } from '@go-client/context/ImageCropperContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { uploadImage } from '@go-client/lib/api/brand';
import { cherrypickImages, createImage, paginateImages } from '@go-client/lib/api/image';
import { mapBy } from '@go-shared/lib/functional';
import { BareImage, Image } from '@go-shared/types/image';
import { populate as populateImage } from '../../../shared/lib/image';
import { SortOrder } from '@go-shared/types/userActionRecord';
import { ObjectID } from 'bson';
import React, { useEffect, useMemo, useState } from 'react';
import BubbleButton from './BubbleButton';
import BubbleDebounce from './BubbleDebounce';
import BubbleFormGroup from './BubbleFormGroup';
import BubbleImageThumbnail from './BubbleImageThumbnail';
import BubbleInput from './BubbleInput';

interface NewImageForm {
  key: string;
}

interface BubbleImageSelectorProps {
  value: string[];
  maxItems?: number;
  readOnly?: boolean;
  onValue: (value: string[]) => void;
}

export default function BubbleImageSelector(props: BubbleImageSelectorProps) {
  const { craftImage } = useImageCropper();
  const { brand } = useServerInfo();

  const [uploadingImg, setUploadingImg] = useState(false);
  const [allImages, setAllImages] = useState<Image[]>([]);
  const [newImage, setNewImage] = useState<NewImageForm | undefined>(undefined);
  const [search, setSearch] = useState('');

  const PER_PAGE = 15;

  async function fetchData() {
    const [searchImages, itemImages] = await Promise.all([
      paginateImages({
        page: 1,
        perPage: PER_PAGE,
        sortBy: 'updatedAt',
        sortDirection: SortOrder.DESC,
        search,
      }),
      cherrypickImages(props.value),
    ]);

    const populatedCherryPickImages = itemImages.map(i => populateImage(i, brand.imageTypes))

    setAllImages([...searchImages.data, ...populatedCherryPickImages]);
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, [search]);

  function showImageForm() {
    setNewImage({ key: '' });
  }

  const dataMap = useMemo(() => mapBy(allImages, 'key'), [allImages]);

  return (
    <div className="w-full">
      <div className="flex items-center flex-wrap gap-2">
        {props.value.map((i, idx) => (
          <BubbleImageThumbnail
            key={`${i}-${idx}`}
            thumbnail={dataMap[i]}
            purpose="list"
            readOnly={props.readOnly}
            className="w-32"
            onDeleteClick={() => {
              props.onValue(props.value.filter((_, i) => i !== idx));
            }}
          />
        ))}
        {props.maxItems === undefined ||
          (props.value.length < props.maxItems && (
            <div
              className="w-32 aspect-square grid items-center justify-center relative"
              style={{ border: '5px dashed #ccc' }}
            >
              <BubbleButton
                variant="smoke"
                inverse
                className="w-16 aspect-square stretched-link outline-none-i"
                appearance="circled"
                onClick={showImageForm}
                disabled={props.readOnly}
                icon="plus"
                iconSize="xl"
              />
            </div>
          ))}
      </div>
      {newImage && (
        <div className="bg-eggshell-dark border border-eggshell-darkest pt-5 px-4 pb-3 mt-2 shadow-lg w-full relative">
          <BubbleButton
            variant="crimson"
            inverse
            onClick={() => setNewImage(undefined)}
            icon="xmark"
            iconSize="xl"
            className="absolute top-0 right-0 p-2-i"
          />
          <div className="grid grid-cols-1 gap-2">
            <BubbleFormGroup label="Image library">
              <div className="flex items-center">
                <BubbleDebounce onTrigger={[setSearch, 500]}>
                  {({ debounce }) => (
                    <BubbleInput
                      type="text"
                      className="flex-grow rounded-right-0-i"
                      inputClassName="rounded-right-0-i"
                      block
                      placeholder="Search..."
                      value={search}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
                <BubbleButton
                  variant="lime"
                  icon="sparkles"
                  className="rounded-left-0-i"
                  disabled={uploadingImg}
                  onClick={() => {
                    craftImage(
                      undefined,
                      'first',
                      undefined,
                      async (artifacts: CraftArtifact[]) => {
                        try {
                          setUploadingImg(true);

                          const [main, ...variants] = artifacts;
                          const files = artifacts.map(
                            artifact =>
                              new File([artifact.blob], [artifact.name, 'webp'].join('.'), {
                                type: artifact.blob.type,
                              })
                          );
                          const [mainRes, ...variantRes] = await Promise.all(
                            files.map(file => uploadImage(brand._id, file))
                          );

                          const newImg: BareImage = {
                            _id: new ObjectID().toHexString(),
                            key: mainRes.location,
                            name: main.name,
                            description: '',
                            extension: 'webp',
                            mimeType: 'image/webp',
                            size: main.size,
                            imageType: [],
                            variants: variants.map((v, idx) => ({
                              _id: new ObjectID().toHexString(),
                              key: variantRes[idx].location,
                              optimization: v.optimization,
                              name: v.name,
                              size: v.size,
                              mimeType: 'image/webp',
                              extension: 'webp',
                              imageType: [],
                            })),
                          };

                          await createImage(newImg);
                          await fetchData().catch(console.error);
                          const newImgs = [...props.value, mainRes.location];

                          props.onValue(newImgs);
                        } finally {
                          setUploadingImg(false);
                        }
                      }
                    );
                  }}
                />
              </div>
            </BubbleFormGroup>
            <div className="flex flex-wrap gap-2">
              {allImages.slice(0, PER_PAGE).map((img, idx) => (
                <BubbleImageThumbnail
                  key={`${img.key}-${idx}`}
                  thumbnail={img}
                  purpose="button"
                  className="w-24"
                  onClick={() => {
                    if (props.maxItems !== undefined && props.value.length < props.maxItems) {
                      props.onValue([...props.value, img.key]);
                    }
                  }}
                />
              ))}
            </div>
            {allImages.length === 0 && (
              <div className="text-center text-slate py-5-i w-full">No items to show</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

